/**
 * Arabic → Latin transliteration map used for auto-generating store slugs
 * from store names. Intentionally lossy — multiple Arabic letters collapse
 * to the same Latin letter (e.g. س / ص → s, ت / ط → t) because the result
 * is a URL slug, not a linguistic romanization. Matches the user's
 * example: هدا → "hda".
 *
 * Tashkeel (diacritics) is stripped before mapping, so "هَذَا" still
 * produces "hda".
 */
const AR_MAP: Record<string, string> = {
  'ا': 'a', 'أ': 'a', 'إ': 'a', 'آ': 'a', 'ٱ': 'a',
  'ب': 'b',
  'ت': 't', 'ث': 'th',
  'ج': 'j',
  'ح': 'h', 'خ': 'kh',
  'د': 'd', 'ذ': 'd',
  'ر': 'r', 'ز': 'z',
  'س': 's', 'ش': 'sh',
  'ص': 's', 'ض': 'd',
  'ط': 't', 'ظ': 'z',
  'ع': 'a', 'غ': 'gh',
  'ف': 'f', 'ق': 'q',
  'ك': 'k', 'ل': 'l',
  'م': 'm', 'ن': 'n',
  'ه': 'h', 'ة': 'h',
  'و': 'w', 'ؤ': 'w',
  'ي': 'y', 'ى': 'y', 'ئ': 'y',
  'ء': '',
};

// Arabic tashkeel/diacritics that mean nothing for a URL slug.
const TASHKEEL_RE = /[ً-ْٰ]/g;

/**
 * Turn a free-form (possibly Arabic) store name into a URL-safe slug.
 * Rules: lowercase Latin/digits/hyphens, max 30 chars, no leading/trailing
 * hyphen. Returns "" if nothing usable was produced.
 */
export function nameToSlug(input: string): string {
  if (!input) return '';
  let out = input.normalize('NFKD').replace(TASHKEEL_RE, '');

  // Map Arabic letters to Latin.
  out = out
    .split('')
    .map((ch) => (AR_MAP[ch] !== undefined ? AR_MAP[ch] : ch))
    .join('');

  // Lowercase, collapse separators to a single hyphen, drop everything else.
  out = out
    .toLowerCase()
    .replace(/[\s_]+/g, '-')
    .replace(/[^a-z0-9-]/g, '')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '')
    .slice(0, 30);

  return out;
}

/** Same as `nameToSlug` but applied to a slug field directly — keeps user
 *  edits valid without re-transliterating Arabic chars (the user has already
 *  committed to typing Latin). */
export function sanitizeSlug(input: string): string {
  return input
    .toLowerCase()
    .replace(/\s+/g, '-')
    .replace(/[^a-z0-9-]/g, '')
    .replace(/-+/g, '-')
    .slice(0, 30);
}
